import { useEffect, useState } from "react";
import type { Words } from "./copy";

/** One rough frame of the drawing: which it is (0 first) of how many. */
interface Sketch {
  url: string;
  index: number;
  of: number;
}

/** The instant preview's waiting screen: their own photo coming to life while
 *  the figurine is drawn, a bar that fills, and a line saying what is
 *  happening.
 *
 *  The bar is an honest estimate, not a measurement. A drawing reports almost
 *  nothing while the model works, so the bar runs on time, eased so it slows
 *  as it goes and never reaches the end on its own, and the phases the server
 *  does know about correct it. It only reaches 100% when the preview has
 *  actually been sent (`done`).
 *
 *  It starts low when the page opens and is paced to what is left of the
 *  usual time, rather than counting from when the request arrived: by the time
 *  the page opens the upload and the thank you page have used five to ten
 *  seconds, and a bar that opened at 30% and then crawled felt slower than one
 *  that opens at 4% and moves. When the page first opened is kept for the
 *  session, so a reload carries on rather than starting again.
 *
 *  When the drawing streams, its rough frames are laid over the photo as they
 *  land, soft-focused and sharpening with each one, so the wait ends on the
 *  figurine rather than on a bar. Each frame is a fact the bar can lean on, so
 *  it moves the bar forward too. The focus only comes fully sharp on "Done!",
 *  and the page then opens on the finished drawing.
 *
 *  The same screen answers a change fixed live ("fix"), inside the card of the
 *  drawing it is about: that drawing goes grey and comes back to colour as the
 *  fix is drawn over it, framed whole rather than cropped, since it is the
 *  thing they are looking at the detail of. */
export default function Drawing({
  words,
  photoUrl,
  since,
  phase,
  sketch,
  done,
  mode = "first",
}: {
  words: Words;
  photoUrl: string | null;
  since: string | null;
  phase: "reading" | "drawing" | "finishing" | null;
  sketch: Sketch | null;
  done: boolean;
  mode?: "first" | "fix";
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (done) return;
    const timer = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [done]);

  const [opened] = useState(() => openedAt(since));
  /* A change fixed on a drawing wider than it is tall (a row of three or four
     pets alone is drawn 3:2): the frame takes that shape, read off the picture
     once it loads, rather than the 2:3 of a standing figure. */
  const [wide, setWide] = useState(false);

  /* The last two frames, so the older stays underneath while the newer fades
     in over it, and a frame is only shown once its bytes are here. */
  const [frames, setFrames] = useState<Sketch[]>([]);
  const [loaded, setLoaded] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!sketch) return;
    setFrames((was) => (was.some((f) => f.url === sketch.url) ? was : [...was, sketch].slice(-2)));
  }, [sketch]);
  const shown = [...frames].reverse().find((f) => loaded.has(f.url)) ?? null;
  const arrived = since ? Date.parse(since) : NaN;
  /* Seconds already gone before the page opened, and so how long is left of
     the usual drawing. */
  const before = Number.isFinite(arrived) ? Math.max(0, (opened - arrived) / 1000) : 0;
  const left = Math.max(MIN_LEFT_SECONDS, (mode === "fix" ? USUAL_FIX_SECONDS : USUAL_SECONDS) - before);
  const seconds = Math.max(0, (now - opened) / 1000);
  const pct = done ? 100 : Math.max(estimate(seconds, left, phase), sketchFloor(shown));

  return (
    <div className={`drawing${mode === "fix" ? " drawing-fix" : ""}${mode === "fix" && wide ? " drawing-wide" : ""}${done ? " drawing-done" : ""}`}>
      <div className="drawing-wrap">
      <div className="drawing-frame" aria-hidden="true">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            className="drawing-photo"
            onLoad={(event) => setWide(event.currentTarget.naturalWidth > event.currentTarget.naturalHeight)}
            /* Grey at the start, full colour as it finishes. */
            style={{ filter: `grayscale(${Math.round(100 - pct)}%) contrast(${0.9 + pct / 1000})` }}
          />
        ) : (
          <div className="drawing-photo drawing-blank" />
        )}
        {frames.map((frame) => (
          <img
            key={frame.url}
            src={frame.url}
            alt=""
            className={`drawing-sketch${loaded.has(frame.url) ? " is-in" : ""}`}
            style={{ filter: `blur(${done ? 0 : blurFor(frame)}px)` }}
            onLoad={() => setLoaded((was) => new Set(was).add(frame.url))}
          />
        ))}
        {!done && <div className="drawing-scan" />}
        {!done && (
          <>
            <span className="drawing-spark" style={{ left: "18%", top: "24%", animationDelay: "0s" }} />
            <span className="drawing-spark" style={{ left: "74%", top: "38%", animationDelay: "0.9s" }} />
            <span className="drawing-spark" style={{ left: "40%", top: "72%", animationDelay: "1.7s" }} />
          </>
        )}
      </div>
      </div>

      <div
        className="drawing-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div className="drawing-fill" style={{ width: `${pct}%` }} />
      </div>
      <p className="drawing-step" aria-live="polite">
        {words.t(mode === "fix" ? fixStepKey(pct, phase, done, shown) : stepKey(pct, phase, done, shown))}
        <span className="drawing-pct">{Math.round(pct)}%</span>
      </p>
    </div>
  );
}

/** How long a reading and a drawing usually take together, from the request
 *  arriving to the preview being sent: 30 to 45 seconds. */
const USUAL_SECONDS = 40;
/** And a change fixed live, from the tap to the new drawing on the page: the
 *  note is read first, and a fix is drawn at more detail than a first draw. */
const USUAL_FIX_SECONDS = 75;
/** However late the page opened, the bar is given at least this long. */
const MIN_LEFT_SECONDS = 20;
/** Where the bar starts, and where the time estimate alone takes it by the
 *  time the usual drawing is done. */
const START = 4;
const AT_USUAL_END = 88;

/** From START, easing out, to AT_USUAL_END after `left` seconds and on
 *  towards 97% for a slow one. The reading of the photos is allowed as far as
 *  60%, so a slow reading no longer holds the bar still; the last step before
 *  sending sets it to at least 92%. */
function estimate(
  seconds: number,
  left: number,
  phase: "reading" | "drawing" | "finishing" | null
): number {
  // e^(-k) = 1 - (AT_USUAL_END - START) / (97 - START), solved for the rate.
  const k = -Math.log(1 - (AT_USUAL_END - START) / (97 - START));
  const eased = START + (97 - START) * (1 - Math.exp((-k * seconds) / left));
  if (phase === "reading") return Math.min(eased, 60);
  if (phase === "finishing") return Math.max(eased, 92);
  return eased;
}

/** When this browser first opened the waiting screen for this request, kept
 *  for the session so that a reload carries on where it was. */
function openedAt(since: string | null): number {
  const now = Date.now();
  if (!since) return now;
  const key = `minime-drawing-opened:${since}`;
  try {
    const kept = Number(sessionStorage.getItem(key));
    if (Number.isFinite(kept) && kept > 0 && kept <= now) return kept;
    sessionStorage.setItem(key, String(now));
  } catch {
    /* private mode: a reload starts the bar again, nothing worse */
  }
  return now;
}

/** How soft a frame is drawn: the first is a suggestion, the last nearly the
 *  picture. Soft on purpose, because an early frame can be a face half-made,
 *  and nobody should meet their child or their late dog that way. */
function blurFor(frame: Sketch): number {
  return Math.max(1.5, (frame.of - frame.index) * 2.5);
}

/** Where a frame puts the bar at the least: the frames are spread evenly
 *  between a third of the way and the point the time estimate aims for. */
function sketchFloor(frame: Sketch | null): number {
  if (!frame) return 0;
  return 30 + ((frame.index + 1) * (AT_USUAL_END - 30)) / (frame.of + 1);
}

function stepKey(
  pct: number,
  phase: "reading" | "drawing" | "finishing" | null,
  done: boolean,
  shown: Sketch | null
): string {
  if (done) return "drawDone";
  if (phase === "finishing" || pct >= 88) return "drawAlmost";
  if (shown) return shown.index === 0 ? "drawSketch" : "drawSharpen";
  if (pct < 25) return "drawRead";
  if (pct < 50) return "drawFace";
  if (pct < 72) return "drawColour";
  return "drawDetail";
}

/** The same steps for a fix: their note is read, rather than their photos,
 *  and it is the drawing they already have that is being worked on. */
function fixStepKey(
  pct: number,
  phase: "reading" | "drawing" | "finishing" | null,
  done: boolean,
  shown: Sketch | null
): string {
  if (done) return "drawDone";
  if (phase === "finishing" || pct >= 88) return "drawAlmost";
  if (shown) return shown.index === 0 ? "drawSketch" : "drawSharpen";
  if (phase === "reading" || pct < 25) return "fixRead";
  if (pct < 60) return "fixDrawing";
  return "fixDetail";
}
